import type { FinancialOverview } from './financial-overview-api';
import { RemunerationColumn } from './RemunerationColumn';
import { RevenueCard } from './RevenueCard';
import { TreasuryCard } from './TreasuryCard';
import { WorkingCapitalEvolutionCard } from './WorkingCapitalEvolutionCard';
import { ExpenseSection } from './ExpenseSection';
import { ProvisionProposalCard, ProfitabilityProposalCard } from './FinancialCompletionCards';
import { ProvisionsSourcesCard, ClientRevenueSourcesCard } from './FinancialKnownSourcesCards';

/** Les deux cartes validées restent visibles en réel, sans afficher de montants fictifs. */
export function FinancialPilotageReal({
  overview,
  onGoal,
  onPeriod,
  onRevenueGoal,
  revenueOverview = overview,
  onRevenuePeriod = onPeriod,
  revenuePeriod,
  revenueError,
  onTreasurySetup,
  onSaveWorkingCapitalGoal,
  csrfToken = '',
  demo = false,
}: {
  readonly overview: FinancialOverview;
  readonly onGoal: () => void;
  readonly onRevenueGoal: () => void;
  readonly revenueOverview?: FinancialOverview;
  readonly onRevenuePeriod?: ((period: string) => void) | undefined;
  readonly revenuePeriod?: string | undefined;
  readonly revenueError?: string | null | undefined;
  readonly onTreasurySetup?: (() => void) | undefined;
  readonly onSaveWorkingCapitalGoal?: ((targetCents: string) => Promise<void>) | undefined;
  readonly csrfToken?: string;
  readonly demo?: boolean;
  readonly onPeriod?: ((period: string) => void) | undefined;
}): React.JSX.Element {
  return (
    <div
      className="finance-columns"
      data-calculation-ms={overview.calculationDurationMs}
      data-calculation-timings={
        overview.calculationTimings ? JSON.stringify(overview.calculationTimings) : undefined
      }
    >
      <div className="finance-column finance-column-group">
        <RemunerationColumn overview={overview} onGoal={onGoal} onPeriod={onPeriod} />
        <h2 id="finance-provisions" className="finance-column__title finance-section-anchor">
          <span aria-hidden="true">“</span>
          <span className="finance-column__split">
            Que dois-je provisionner <span>&amp; payer ?</span>
          </span>
        </h2>
        {demo ? <ProvisionProposalCard demo /> : <ProvisionsSourcesCard overview={overview} />}
      </div>
      <section
        className="finance-column finance-column--revenue"
        aria-label="Mon chiffre d’affaires"
      >
        <h2 id="finance-revenue" className="finance-column__title finance-section-anchor">
          <span aria-hidden="true">“</span>Comment progresse mon CA ?
        </h2>
        <RevenueCard
          overview={revenueOverview}
          onGoal={onRevenueGoal}
          onPeriod={onRevenuePeriod}
          selectedPeriod={revenuePeriod}
          periodError={revenueError}
        />
        <h2
          id="finance-expenses"
          className="finance-column__title finance-section-anchor finance-column__next"
        >
          <span aria-hidden="true">“</span>Où part mon argent ?
        </h2>
        <ExpenseSection overview={overview} csrfToken={csrfToken} demo={demo} />
      </section>
      <section
        className="finance-column finance-column--treasury"
        aria-label="Comment évolue ma tréso"
      >
        <h2 id="finance-treasury" className="finance-column__title finance-section-anchor">
          <span aria-hidden="true">“</span>Comment évolue ma tréso ?
        </h2>
        <TreasuryCard overview={overview} onSetup={onTreasurySetup ?? (() => undefined)} />
        <WorkingCapitalEvolutionCard
          overview={overview}
          demo={demo}
          onSaveGoal={onSaveWorkingCapitalGoal}
        />
        <h2
          id="finance-profitability"
          className="finance-column__title finance-section-anchor finance-column__next"
        >
          <span aria-hidden="true">“</span>Quels projets et clients sont rentables ?
        </h2>
        {demo ? (
          <ProfitabilityProposalCard demo />
        ) : (
          <ClientRevenueSourcesCard overview={overview} />
        )}
      </section>
    </div>
  );
}
